/**
 * Block-editor-preview-only: lets an admin drag a card in the Recommended
 * Events block's preview to reorder it, persisting the result into that
 * block's own "Manual Card Order" ACF field (manual_order, comma-separated
 * event post IDs) via `wp.data.dispatch('core/block-editor').updateBlockAttributes()`
 * -- the same native Gutenberg attribute-save flow any other block field
 * uses, so it saves with the page and applies on the live front end too
 * (jctc_apply_manual_event_order(), inc/theme-functions.php).
 *
 * Only events already present in the pinned order stay pinned; any event
 * NOT in that list (including ones added after the admin last reordered)
 * keeps flowing in by date -- see that PHP function's own doc comment for
 * the exact merge rule. This file's only job is turning a drag gesture into
 * an ordered list of event IDs and writing it to the block attribute; the
 * actual sort/merge logic lives entirely server-side.
 *
 * Built on plain Pointer Events (pointerdown/pointermove/pointerup), NOT the
 * native HTML5 Drag-and-Drop API (draggable="true"/dragstart/dragover/drop)
 * -- an earlier version used that API and never actually moved a card with a
 * real mouse despite dragstart firing (the card visibly dimmed). Two
 * distinct, confirmed interferers were found for it, both upstream of this
 * module's own code and neither fixable from here:
 *   1. Swiper itself applies `transform: translate3d(...)` directly to
 *      .swiper-wrapper (present even at slide 1, where the offset is inert)
 *      -- Chromium's native drag-and-drop tracks cursor/drop-target
 *      coordinates unreliably under a transformed ancestor.
 *   2. Gutenberg's own block-editor__container has BOTH capture- and
 *      bubble-phase dragenter/dragover/dragstart/drop listeners (its native
 *      drag-and-drop machinery for dragging files/blocks onto the canvas),
 *      confirmed via CDP DOMDebugger.getEventListeners() sitting between
 *      this module's target and the document root, and confirmed (via a
 *      dispatched dragenter event) to call preventDefault() on the way down
 *      -- a second, independent system with its own opinion about the same
 *      native drag session.
 * Pointer Events sidestep both: there is no native drag session for
 * anything to intercept, `translate3d` on an ancestor has no effect on
 * pointermove coordinates, and Gutenberg's dropzone machinery only listens
 * for drag* events, never pointer* ones. This also makes the gesture
 * testable with an ordinary synthesized mouse sequence (mousedown/move/up),
 * unlike native HTML5 drag target events, which tooling reliably fails to
 * deliver.
 *
 * The target elements (.js-recommended-slides-wrapper, .js-recommended-slide)
 * only exist in blocks/recommended-events/render.php's own markup when
 * $is_preview is true, so this module is a silent no-op on the live front
 * end.
 */

const WRAPPER_SELECTOR = '.js-recommended-slides-wrapper';
const SLIDE_SELECTOR = '.js-recommended-slide';
const DRAGGING_CLASS = 'is-dragging';
// A real (position: static) flex child that stands in for the dragged card
// at its CANDIDATE drop slot while the drag is in progress -- the dragged
// card itself is position: absolute for the whole gesture (so it can track
// the cursor 1:1, see .is-dragging's own SCSS doc comment) and therefore
// consumes no flex space wherever it currently sits in the DOM; moving IT
// around during the drag was confirmed to visibly do nothing to its
// siblings at all (getSlideLayout() already excludes anything with
// DRAGGING_CLASS from its own math, by design, so the "other" cards'
// computed positions never change regardless of where the absolutely
// positioned card's own DOM node currently sits) -- no gap ever opened up
// to hint where a drop would land. This placeholder is what actually moves
// through the siblings during the drag instead; being a normal flex child,
// inserting it pushes the real neighbors apart via the exact same
// flipSiblings() animation, which is the visual cue this was missing.
const PLACEHOLDER_CLASS = 'js-recommended-slide-placeholder';
// Below this many pixels of pointer movement, a press-and-release still
// counts as a plain click (opening the event's admin fields panel, see
// modules/events.js) instead of a reorder drag -- matches common drag-lib
// convention, comfortably above accidental hand tremor on a mousedown.
const DRAG_THRESHOLD_PX = 6;

// The admin preview's own .js-editor-preview-scale ancestor (events-ajax.js's
// initEventsPreviewScale()) shrinks this whole block with CSS `zoom` so its
// container-query rules still resolve at their real desktop breakpoint --
// `getComputedStyle(el).zoom` only ever reports the value set ON that exact
// element (confirmed live: 1 on any descendant, 0.583333 only on the
// .js-editor-preview-scale element itself), never an ancestor's compounded
// effect, unlike most CSS properties. That mismatched the drag's own
// translate() math for a genuinely different reason than Swiper's
// translate3d/Gutenberg's dropzone machinery above: `event.clientX` is
// always real, un-zoomed viewport pixels, but a `transform: translate(Npx)`
// written on a descendant of a zoomed ancestor only ever moves N * zoom
// visual pixels -- so writing the cursor's own raw pixel delta straight
// into transform undershot every move by the same ~0.583 factor, and
// confirmed in the field as the card visibly drifting away from the cursor
// the longer/further a drag ran. Dividing by this factor before writing
// any translate distance -- both here and in flipSiblings()'s own
// sibling-slide animation -- cancels the shrink back out.
const getEffectiveZoom = (el) => {
    let zoom = 1;
    let node = el;
    while (node && node !== document.body) {
        zoom *= parseFloat(getComputedStyle(node).zoom) || 1;
        node = node.parentElement;
    }
    return zoom;
};

// Reaches into the outer editor window's wp.data the same way
// modules/events.js's openAdminEventPanel() does -- see that file's own doc
// comment for why both same-window and genuinely-iframed canvases need
// handling (this theme's apiVersion-2 ACF blocks render same-window today,
// but this stays correct either way).
const getEditorWp = () => (window.parent !== window ? window.parent.wp : window.wp);

// Every candidate's SETTLED (natural-layout) left edge, computed purely
// analytically from track's own rect + DOM order + a fixed width/gap --
// NOT read via getBoundingClientRect() on the candidates themselves. That
// was tried first (with a "clear any in-flight FLIP transform, measure,
// restore" trick for a sibling mid-flipSiblings() animation) and confirmed
// unreliable in the field: once a sibling's own rAF callback had already
// swapped it from an inline transform to the real CSS transition
// (FLIP_TRANSITION), synchronously writing style.transform to 'none' and
// immediately back mid-transition left the browser's own transition engine
// reporting an incorrect, still-drifting position on every subsequent read
// -- confirmed via a live trace showing a stationary sibling's "measured"
// left edge changing on every single poll for the rest of that drag, with
// no further DOM mutation to explain it. Track order + a fixed per-slide
// width/gap has no such dependency: it's exactly what every slide's
// position WILL be once any in-flight animation finishes, independent of
// wherever that animation currently visually is. Width/gap are read off
// the first candidate purely for their own (transform-independent) values,
// not its position.
const getSlideLayout = (track) => {
    const slides = Array.from(track.querySelectorAll(SLIDE_SELECTOR)).filter(
        (el) => !el.classList.contains(DRAGGING_CLASS)
    );
    const positions = new Map();
    if (!slides.length) {
        return { positions, width: 0 };
    }
    const trackRect = track.getBoundingClientRect();
    const width = slides[0].getBoundingClientRect().width;
    // marginRight (Swiper's own spaceBetween implementation -- confirmed via
    // its own inline style, "margin-right: 24px") is a plain CSS length, in
    // the element's own logical pixels -- getComputedStyle() never adjusts
    // it for the admin preview's ambient `zoom`, unlike getBoundingClientRect(),
    // which already reports `width` and `trackRect` in real, zoomed visual
    // pixels. Multiplying by getEffectiveZoom() converts it into the SAME
    // visual-pixel units as everything else this function computes with --
    // left unscaled, the accumulated gap-vs-width unit mismatch grew with
    // every slide walked past, confirmed in the field as exactly that:
    // candidates close to the track's own start were only slightly off,
    // and the error compounded into a very visible cursor-vs-card gap by
    // the 3rd/4th slide.
    const gap =
        (parseFloat(getComputedStyle(slides[0]).marginRight) || 0) * getEffectiveZoom(track);
    let left = trackRect.left;
    slides.forEach((slide) => {
        positions.set(slide, left);
        left += width + gap;
    });
    return { positions, width };
};

// Standard "find the sibling to insert before" drag-reorder algorithm:
// among every slide except the one currently being dragged, picks the one
// whose horizontal center is the closest one still to the right of the
// cursor -- inserting the dragged slide there reads as "drop it here"
// regardless of whether the cursor is over that slide's left or right half.
const getSlideAfterCursor = (track, cursorX, excludeEl) => {
    const { positions, width } = getSlideLayout(track);
    // excludeEl: the placeholder itself, while it's being moved -- without
    // this it would be a candidate to "insert before/after itself", which
    // only ever wastes a redundant reorder at best and can pin it in place
    // at worst (its own position always registers a zero offset from
    // itself).
    if (excludeEl) {
        positions.delete(excludeEl);
    }
    let closest = { offset: Number.NEGATIVE_INFINITY, element: null };
    positions.forEach((left, slide) => {
        const offset = cursorX - left - width / 2;
        if (offset < 0 && offset > closest.offset) {
            closest = { offset, element: slide };
        }
    });
    return closest.element;
};

// ACF's ServerSideRender can have an EARLIER preview fetch still in flight
// (any attribute change, including ones unrelated to this drag, kicks one
// off) when this dispatches its own updateBlockAttributes() -- Gutenberg's
// own ServerSideRender component doesn't cancel/ignore a stale in-flight
// request when a newer attribute change supersedes it (a confirmed,
// long-standing upstream gap -- WordPress/gutenberg#7320 asks for exactly
// this debounce/cancellation and remains open), so if that earlier request
// resolves AFTER this one, its response -- rendered from whatever
// manual_order was current when IT was sent, not this drag's -- wins the
// race and Gutenberg swaps it in, visibly reverting both the DOM order and
// the just-set attribute back to a stale value. Confirmed in the field via
// an early (before-any-script) Node.prototype.insertBefore/appendChild/
// innerHTML= patch that caught nothing else touching this subtree, yet the
// order (and once, an unrelated, already-unset "Recommended Event" flag)
// still reverted ~300-500ms after drop -- a live network response
// overwriting the DOM is the only mechanism consistent with THAT (a stale
// flag reappearing is data, not a leftover transform/position).
//
// The general fix (confirmed against how sortable-list implementations
// handle a server-driven preview more broadly: dispatch the real update
// exactly once and let local state stay the visual source of truth,
// rather than re-dispatching and racing the same request against itself)
// is to send the attribute change exactly once, then separately watch for
// and -- if some out-of-order response DID land -- silently correct the
// rendered DOM back to what this drag actually decided, purely locally (no
// further network round trip, since the persisted attribute itself is
// already correct and is what a reload/Update actually saves).
//
// A fixed-delay poll (an earlier version of this used a single
// window.setTimeout(..., 700)) technically got the DATA right but was
// visibly rough: the stale response's own revert stayed on screen for the
// whole 700ms before an un-animated appendChild() snapped it back,
// reading as the dragged card "jumping" through 2 slots, pausing, then
// jumping again -- confirmed described exactly that way in the field. A
// MutationObserver reacts to the SAME instant the stale response's own DOM
// write lands (observer callbacks run as a microtask right after the
// mutation, well under a frame) instead, and the correction itself runs
// through flipSiblings() -- the SAME slide-into-place animation an ordinary
// drag reorder uses -- instead of an instant reparent, so even the
// correction itself reads as one more smooth slide rather than a snap.
//
// The observer watches `wrapperEl` (.js-recommended-slides-wrapper) with
// subtree:true, NOT a captured reference to .swiper-wrapper (the actual
// slide track) directly -- confirmed in the field as the real reason an
// earlier version of this observer sometimes never fired at all: a stale
// ServerSideRender response replaces .swiper-wrapper ITSELF with a fresh
// element (Swiper's own re-init tears down and rebuilds its wrapper when
// the container's inner markup changes), which silently detaches whatever
// node an observer was watching directly. .js-recommended-slides-wrapper
// one level up is the one confirmed to survive every one of these
// replacements untouched, so correctIfNeeded() below re-queries the
// CURRENT .swiper-wrapper fresh on every firing instead of trusting a
// closed-over reference that may already be stale.
// Generous on purpose -- confirmed in the field as a real, not merely
// theoretical, need: a "first attempt reverts, identical second attempt
// works" pattern is consistent with the FIRST attempt's own dispatch
// racing an even earlier in-flight fetch (e.g. the block's own initial
// mount-time preview request) that takes longer than a quick dev-server
// round trip to resolve. 2 seconds tracked correctly in this repo's own
// (fast, local) environment but wasn't safely above every real-world
// latency this can see.
const SETTLE_WINDOW_MS = 8000;

// One active settle-correction observer per wrapper at a time, keyed by the
// wrapper element -- confirmed as a real bug, not just a theoretical risk:
// two drags on the same carousel within the same SETTLE_WINDOW_MS (routine
// for a normal "drag a few cards around" session, and unavoidable in
// automated repeat-testing) left the FIRST drag's own observer still
// running when the SECOND drag committed its own, different order. That
// first observer's correctIfNeeded() only ever knows about ITS OWN drag's
// intended `ids`, so it read the second drag's legitimate new order as
// "wrong" and reverted the DOM straight back to the first drag's result --
// which read, from the second drag, as it simply not doing anything at
// all. Disconnecting whatever observer the previous call left running
// before starting a new one means only the MOST RECENT drag's own idea of
// the correct order is ever being enforced.
const activeSettleObservers = new WeakMap();

const persistOrder = (wrapperEl) => {
    const targetWp = getEditorWp();
    if (!targetWp?.data?.dispatch) {
        return;
    }

    const clientId = wrapperEl.closest('[data-block]')?.getAttribute('data-block');
    if (!clientId) {
        return;
    }

    const ids = Array.from(wrapperEl.querySelectorAll(SLIDE_SELECTOR))
        .map((slide) => slide.dataset.eventId)
        .filter(Boolean);

    if (!ids.length) {
        return;
    }

    const currentAttributes = targetWp.data
        .select('core/block-editor')
        ?.getBlockAttributes(clientId);

    // ACF blocks store every field's value under attributes.data, keyed by
    // field name, alongside an underscore-prefixed companion key mapping to
    // that field's own key (ACF's own convention -- see
    // advanced-custom-fields-pro/pro/blocks.php's `$values['_' . $field['name']]
    // = $field['key']`) -- required for ACF to recognize and persist this as
    // a real field value on save, not an arbitrary attribute.
    const nextData = {
        ...(currentAttributes?.data || {}),
        manual_order: ids.join(','),
        _manual_order: 'field_jctc_recommended_events_block_manual_order',
    };

    targetWp.data.dispatch('core/block-editor').updateBlockAttributes(clientId, { data: nextData });

    activeSettleObservers.get(wrapperEl)?.disconnect();

    let correcting = false;

    const correctIfNeeded = () => {
        if (correcting) {
            return;
        }
        const track = wrapperEl.querySelector('.swiper-wrapper');
        if (!track) {
            return;
        }
        const liveSlides = Array.from(track.querySelectorAll(SLIDE_SELECTOR));
        const liveIds = liveSlides.map((slide) => slide.dataset.eventId).filter(Boolean);
        if (liveIds.length === ids.length && liveIds.every((id, i) => id === ids[i])) {
            return;
        }
        correcting = true;
        // Instant, not FLIP-animated -- this only ever fires after the drag
        // itself is long over (Gutenberg's own stale-SSR-fetch revert, see
        // this function's own doc comment above), so by the time it runs the
        // user is no longer watching a live drag at all, just whatever's on
        // screen. An earlier version FLIP-animated this exactly like a live
        // reorder does, but the user's own explicit feedback (after the drop
        // itself was made instant, for the same reason) was that THIS
        // correction's own glide was the more confusing half of the two: a
        // sibling visibly snapping back to its stale, reverted position for
        // one frame and then gliding back to the correct one reads as a
        // glitch, not a deliberate animation, precisely because nothing
        // about it was user-driven. Defensively cleared on every slide here
        // too (not just relying on flipSiblings() clearing its own leftover
        // transition on its own timer) -- belt-and-suspenders against the
        // same class of bug: if this correction ever lands WHILE an earlier
        // FLIP's own cleanup timeout hasn't fired yet, a stale
        // `transition: transform` would otherwise still be sitting on the
        // element when appendChild() below changes its layout position.
        liveSlides.forEach((slide) => {
            slide.style.transition = '';
            slide.style.transform = '';
        });
        const byId = new Map(liveSlides.map((slide) => [slide.dataset.eventId, slide]));
        reassertOrder(track, ids, (id) => byId.get(id));
        correcting = false;
    };

    // Called directly from the observer, NOT debounced -- an earlier version
    // debounced this by 60ms specifically because correctIfNeeded() used to
    // call flipSiblings() for its own correction, and its own corrective
    // track.appendChild() calls are themselves childList mutations on the
    // exact subtree this same observer watches, so a direct
    // MutationObserver(correctIfNeeded) re-fired itself off its own
    // correction -- each extra firing restarted every slide's transform
    // transition from whatever (possibly mid-flight) position it currently
    // was, compounding into a visible overshoot. correctIfNeeded() is
    // instant now (no transition, see its own doc comment -- the user's own
    // explicit feedback was that ANY animated correction reads as an
    // unexplained flicker, since nothing about it is user-driven), so a
    // self-triggered re-fire now just re-checks an already-correct DOM and
    // no-ops immediately -- nothing left to compound. Reacting synchronously
    // instead of after an artificial 60ms wait matters here specifically
    // BECAUSE there's no animation to smooth over the wait: the whole point
    // of instant correction is to keep Gutenberg's own occasional stale-SSR
    // revert (see correctIfNeeded's own doc comment) on screen for as few
    // frames as possible -- debouncing it would just hold the wrong state
    // on screen for the debounce's own duration first, for no benefit.
    const observer = new MutationObserver(correctIfNeeded);
    // attributes: true, attributeFilter: ['data-event-id'] -- NOT just
    // childList. Confirmed in the field via a raw document-level attribute
    // spy (attributeOldValue: true) as the actual, exact mechanism behind
    // the drag-order revert this whole observer exists to catch: the two
    // affected slide elements' own data-event-id VALUES get swapped in
    // place (oldValue "1227"->"1228" on one node, "1228"->"1227" on the
    // other, both logged with the SAME timestamp), never touching childList
    // at all -- no node is ever added, removed, or reordered. A
    // childList-only observer (this one's own original config) can never
    // fire for that, which is exactly why it never once caught this in
    // testing despite the visible revert happening reliably. Whatever
    // upstream mechanism does this (still unconfirmed -- worth investigating
    // further, but not blocking, since watching for its actual effect
    // works regardless of its cause), correctIfNeeded() re-reads each
    // slide's live dataset.eventId either way, so it corrects an
    // attribute-swap exactly the same way it corrects a real reorder.
    observer.observe(wrapperEl, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-event-id'],
    });

    activeSettleObservers.set(wrapperEl, observer);
    window.setTimeout(() => {
        observer.disconnect();
        if (activeSettleObservers.get(wrapperEl) === observer) {
            activeSettleObservers.delete(wrapperEl);
        }
    }, SETTLE_WINDOW_MS);
};

// A genuine drag ending still fires an ordinary 'click' afterward on
// whatever element the pointer is currently over -- swallowed once, at the
// capture phase, so it never reaches modules/events.js's own click handling
// and pops open the event fields panel right after a reorder.
const suppressNextClick = (el) => {
    const handler = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };
    el.addEventListener('click', handler, { capture: true, once: true });
};

// FLIP (First-Last-Invert-Play): track.insertBefore()/appendChild() above
// move a sibling to its new slot instantly -- this makes that jump read as a
// smooth slide instead. Called on every sibling still in the track (the
// dragged element itself is animated separately, via onPointerMove's own
// cursor-following transform) right after the DOM mutation that changed
// their slots: "First" is the position passed in (captured before the
// mutation, a plain getBoundingClientRect() -- FLIP's own "First" means
// wherever the element visually IS right now, in-flight animation
// included, so this is correct as-is), "Last" is each slide's own
// analytical position from getSlideLayout() (its own doc comment explains
// why NOT a live getBoundingClientRect() read here) for the SAME reason
// getSlideAfterCursor() needs it: the settled position an in-flight FLIP
// is headed toward, not wherever it currently visually is. `zoom` is
// getEffectiveZoom() (above) at the caller's own draggedEl -- see that
// function's own doc comment for why a translate() distance written under
// a zoomed ancestor needs dividing by it.
const FLIP_TRANSITION = 'transform 220ms cubic-bezier(0.2, 0, 0.2, 1)';

const flipSiblings = (track, siblings, firstRects, zoom) => {
    const { positions: lastPositions } = getSlideLayout(track);
    siblings.forEach((el) => {
        const first = firstRects.get(el);
        const lastLeft = lastPositions.get(el);
        if (!first || lastLeft === undefined) {
            return;
        }
        const deltaX = (first.left - lastLeft) / zoom;
        if (!deltaX) {
            el.style.transition = '';
            el.style.transform = '';
            return;
        }
        el.style.transition = 'none';
        el.style.transform = `translateX(${deltaX}px)`;
        // Force layout so the browser commits the "start from First" frame
        // before the next frame clears it -- without this the two style
        // writes can get batched into one paint and skip straight to Last,
        // silently dropping the animation.
        // eslint-disable-next-line no-unused-expressions
        el.offsetWidth;
        requestAnimationFrame(() => {
            el.style.transition = FLIP_TRANSITION;
            el.style.transform = '';
            // Cleared once the transition's own duration has actually
            // elapsed, not left set indefinitely -- confirmed in the field
            // as a real, visible bug, not just untidy inline style: an
            // element's `transition: transform` here has NO reason to ever
            // be cleared again on its own once this card stops moving, so
            // without this it sits on the element permanently. Any LATER,
            // completely unrelated transform change to that same element --
            // this module's own instant settle-correction reorder
            // (persistOrder's correctIfNeeded(), deliberately NOT animated,
            // see its own doc comment) included -- would then pick up this
            // stale transition and animate anyway, reading as an
            // unexplained flicker/glide on a card nothing was actively
            // dragging.
            window.setTimeout(() => {
                el.style.transition = '';
            }, 220);
        });
    });
};

// Reorders `track`'s children to match `orderedIds` exactly, via a plain
// track.appendChild() per id (instant, no animation) -- shared by
// persistOrder's own settle-correction (correctIfNeeded) and the live-drag
// guard (liveGuardObserver's callback in attachWrapper), which previously
// each reimplemented an identical "look up the element for this id, then
// appendChild it" loop. `resolveElement(id)` is the only part that
// genuinely differs between the two callers (correctIfNeeded resolves every
// id from a plain id->element map; the live guard also has to map the
// dragged card's own id back to its placeholder instead of the real,
// out-of-flow card element) -- keeping that as an injected function instead
// of duplicating the whole loop means a fix to the mechanic itself (e.g. the
// missing id silently skipped rather than throwing) only ever needs making
// once.
const reassertOrder = (track, orderedIds, resolveElement) => {
    orderedIds.forEach((id) => {
        const slide = resolveElement(id);
        if (slide) {
            track.appendChild(slide);
        }
    });
};

const attachWrapper = (wrapperEl) => {
    // let, not const -- an ACTIVE drag can outlive this specific .swiper-wrapper
    // node. persistOrder's own settle-correction observer already watches
    // wrapperEl instead of track for exactly this reason (see its own doc
    // comment: "a stale ServerSideRender response replaces .swiper-wrapper
    // ITSELF with a fresh element"); onPointerMove below re-checks this
    // binding on every move and cancels the drag rather than silently
    // operating against a since-detached track if a re-render (e.g. an
    // unrelated card's admin-panel batch save calling
    // refreshEventPreviewBlocks() on this SAME block) swaps it out mid-drag.
    const track = wrapperEl.querySelector('.swiper-wrapper');
    if (!track || track.dataset.jctcReorderBound) {
        return;
    }
    track.dataset.jctcReorderBound = '1';

    let draggedEl = null;
    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let isDragging = false;
    // Last "insert before this slide" target actually applied to the DOM --
    // re-running insertBefore/appendChild on every single pointermove (most
    // of which don't cross any other slide's center at all) would restart
    // flipSiblings()'s own transition each time for no reason; only acting
    // on an actual change keeps each slide's slide-into-place animation
    // uninterrupted for its full 220ms.
    let lastAfterEl;
    // Where the cursor sits WITHIN the card, captured once at drag start
    // (draggedEl.getBoundingClientRect() there, before any transform is ever
    // applied, so it's a true natural-layout offset) and never touched again
    // for the rest of the drag. onPointerMove uses this fixed offset plus
    // the cursor's OWN current position to recompute the card's transform
    // from scratch on every single move -- see that function's own doc
    // comment for why "from scratch every frame" replaced an earlier
    // version that instead accumulated a running correction term across
    // reorders (extraOffsetX/Y): that accumulator's own math was only ever
    // as correct as every single prior frame's calculation, so one bad
    // frame (a mid-FLIP rect, a reorder landing between two measurements,
    // anything) stayed wrong for the rest of that drag instead of
    // self-correcting on the very next frame the way this version does.
    let grabOffsetX = 0;
    let grabOffsetY = 0;
    // getEffectiveZoom() (above) at drag start -- computed once per drag,
    // not on every move, since it only changes on a window resize (which
    // isn't happening mid-gesture) and re-walking the ancestor chain every
    // pointermove would be pure waste.
    let zoom = 1;
    // The gap-indicator element (see PLACEHOLDER_CLASS's own doc comment) --
    // created fresh at the start of every drag, moved through the siblings
    // in draggedEl's place, and swapped back out for draggedEl itself at
    // drop. null whenever no drag is in progress.
    let placeholderEl = null;

    // Guards an ACTIVE drag's own live reordering against a foreign DOM
    // mutation landing mid-gesture -- confirmed in the field as a real,
    // deterministic (not flaky) failure mode: dragging a card immediately
    // after a previous drag on the same carousel, the FIRST drag's own
    // attribute dispatch (persistOrder) kicks off Gutenberg's ServerSideRender
    // re-fetch for the block preview, and when that response lands (~400-600ms
    // later, well within a second drag's own gesture), ACF/Gutenberg's own
    // DOM patching calls insertBefore() DIRECTLY on the sibling slides to
    // match the freshly server-rendered (first drag's own, already-correct)
    // markup -- confirmed via a raw Node.prototype.insertBefore spy showing
    // exactly these calls, attributed to neither this module's own reorder
    // logic (which only ever moves the DRAGGED element, never siblings
    // relative to each other) nor persistOrder's settle-correction observer
    // (deliberately disconnected the moment THIS drag starts, to stop the
    // FIRST drag's own correction fighting this one -- see that disconnect
    // call's own doc comment). Both drags are legitimate; the fix is neither
    // blocking the incoming patch nor re-enabling the settle observer mid-drag,
    // but reasserting THIS drag's own live intent whenever a mutation this
    // code didn't itself just make is observed.
    let liveGuardObserver = null;
    let expectedOrderIds = [];
    // While a drag is active, placeholderEl -- not draggedEl -- is what
    // actually moves through the real siblings (see PLACEHOLDER_CLASS's own
    // doc comment), so it's what stands in for draggedEl's id here: reading
    // draggedEl's own (unmoving, off in whatever DOM slot the drag started
    // from) position directly would report a live order that never reflects
    // where the drag actually currently intends to drop. Outside an active
    // drag both are null and this reduces to a plain per-slide id list.
    const getLiveOrderIds = () =>
        Array.from(track.querySelectorAll(SLIDE_SELECTOR))
            .map((slide) => {
                if (slide === placeholderEl) {
                    return draggedEl?.dataset.eventId;
                }
                if (slide === draggedEl) {
                    return null;
                }
                return slide.dataset.eventId;
            })
            .filter(Boolean);

    // Bound to `document`, not the dragged slide itself -- the slide is
    // exactly the element this drag repeatedly reparents (track.appendChild/
    // insertBefore below, every move), and the Pointer Events spec releases
    // an element's pointer capture the moment it's removed from the DOM
    // tree, even for a same-position appendChild that only LOOKS like a
    // no-op. Confirmed in the field via a raw document-level spy: capture
    // set on the slide kept delivering pointermove for the first couple of
    // moves, then silently stopped the moment the cursor crossed outside
    // the (now-detached-and-reinserted) slide's own bounds -- exactly the
    // "card dims, then never actually reaches the drop target" symptom.
    // document-level listeners have no such dependency: they receive every
    // pointermove for the pointer's whole lifetime regardless of which
    // element the cursor is currently over or how many times the dragged
    // node gets moved around the DOM mid-gesture.
    //
    // Abandons an in-progress drag WITHOUT committing anything -- used only
    // when onPointerMove (below) detects its own `track` binding has gone
    // stale (a re-render replaced .swiper-wrapper out from under an active
    // drag; see `track`'s own `let` doc comment above for the confirmed real
    // scenario). Deliberately does NOT call persistOrder() or attempt the
    // placeholder->draggedEl swap onPointerUp's normal path does: both would
    // operate against the OLD, now-detached track, either writing into dead
    // DOM or reaching for a placeholder that was never part of the live,
    // freshly-rendered subtree at all. The safe fallback is losing this one
    // drag's own result cleanly (the user can simply drag again) rather than
    // risking a silent wrong-order write.
    const cancelDrag = () => {
        liveGuardObserver?.disconnect();
        liveGuardObserver = null;
        document.removeEventListener('pointermove', onPointerMove);
        document.removeEventListener('pointerup', onPointerUp);
        document.removeEventListener('pointercancel', onPointerUp);
        draggedEl = null;
        pointerId = null;
        isDragging = false;
        lastAfterEl = undefined;
        placeholderEl = null;
    };

    const onPointerMove = (event) => {
        if (!draggedEl || event.pointerId !== pointerId) {
            return;
        }

        // Re-checked on every move, not just at drag start -- track was
        // captured (or last replaced) at some earlier point in time, and a
        // re-render unrelated to this drag can swap .swiper-wrapper out at
        // any moment while the pointer is still down. A single-element
        // querySelector here is negligible next to the querySelectorAll
        // work getSlideAfterCursor()/flipSiblings() already do every move.
        const liveTrack = wrapperEl.querySelector('.swiper-wrapper');
        if (liveTrack !== track) {
            cancelDrag();
            return;
        }

        if (!isDragging) {
            if (
                Math.abs(event.clientX - startX) < DRAG_THRESHOLD_PX &&
                Math.abs(event.clientY - startY) < DRAG_THRESHOLD_PX
            ) {
                return;
            }
            isDragging = true;
            // Measured BEFORE the .is-dragging class below -- that class
            // switches draggedEl to position: absolute (see its own SCSS
            // doc comment for why), which redefines what "natural,
            // untransformed position" even means for it (from "wherever
            // flex layout puts it among its siblings" to "track's own
            // top-left corner, always, since top/left are pinned to 0").
            // Reading this rect AFTER that switch would capture the
            // cursor's offset from the TRACK's corner instead of from
            // wherever inside the card it was actually grabbed.
            const naturalRect = draggedEl.getBoundingClientRect();
            grabOffsetX = event.clientX - naturalRect.left;
            grabOffsetY = event.clientY - naturalRect.top;

            draggedEl.classList.add(DRAGGING_CLASS);
            // No transition while actively dragging -- see this rule's own
            // doc comment in _recommended-events.scss for why (a settle
            // transition gets set explicitly, inline, on drop instead).
            draggedEl.style.transition = 'none';
            lastAfterEl = undefined;
            zoom = getEffectiveZoom(draggedEl);

            // Marks draggedEl's own vacated slot -- inserted right where it
            // still physically sits in the DOM at this exact moment, one line
            // before DRAGGING_CLASS takes it out of flow entirely. swiper-slide
            // (not just SLIDE_SELECTOR) is required for the placeholder to get
            // the SAME width as a real card (that sizing rule targets
            // .swiper-slide, in _recommended-events.scss); the inline
            // margin-right copies Swiper's own per-slide gap (its own inline
            // style, not a CSS rule this stylesheet controls), which
            // getSlideLayout() reads directly off whichever slide happens to
            // be first -- without a matching value here, the track's own
            // analytical gap math goes wrong the instant the placeholder ends
            // up first.
            placeholderEl = document.createElement('div');
            // wp-swiper__slide included too, not just swiper-slide -- every real
            // card carries both (Swiper's own class plus this theme's WP Swiper
            // wrapper's own one), so a future rule written against either alone
            // would otherwise silently skip the placeholder.
            placeholderEl.className = `wp-swiper__slide swiper-slide ${SLIDE_SELECTOR.slice(1)} ${PLACEHOLDER_CLASS}`;
            placeholderEl.style.marginRight = getComputedStyle(draggedEl).marginRight;
            placeholderEl.setAttribute('aria-hidden', 'true');
            track.insertBefore(placeholderEl, draggedEl);
            // A PREVIOUS drag's own settle-correction observer (persistOrder's
            // own, below) only ever got disconnected at the NEXT drag's own
            // drop -- confirmed in the field as a real gap for two drags
            // started in quick succession (a real, ordinary way to use this
            // feature, not just a stress test): if the first drag's delayed
            // stale-response correction lands WHILE this second drag is
            // still actively being dragged (its own persistOrder/disconnect
            // call doesn't happen until ITS OWN drop), that first observer's
            // correctIfNeeded() can fire mid-gesture and yank the DOM back
            // toward the FIRST drag's own intended order, fighting the
            // second drag's live reordering. Disconnecting here, the moment
            // a new drag actually starts, closes that gap -- exactly one
            // thing (either an active drag, or one settle-observer with
            // nothing else touching the DOM) ever owns this track's order.
            activeSettleObservers.get(wrapperEl)?.disconnect();

            // Started fresh for every drag (not left running between drags)
            // -- reads as "this drag's own live intent" for as long as it's
            // dragging, watching the exact same subtree correctIfNeeded()
            // itself watches. childList only: unlike persistOrder's own
            // settle-correction, this doesn't need attributes/data-event-id
            // (Gutenberg's own attribute-swap revert, bug this module
            // already handles elsewhere) -- the DOM-patch collision this
            // guards against is childList (insertBefore) by its own
            // confirmed nature.
            expectedOrderIds = getLiveOrderIds();
            liveGuardObserver = new MutationObserver(() => {
                const liveIds = getLiveOrderIds();
                if (
                    liveIds.length === expectedOrderIds.length &&
                    liveIds.every((id, i) => id === expectedOrderIds[i])
                ) {
                    return;
                }
                const byId = new Map(
                    Array.from(track.querySelectorAll(SLIDE_SELECTOR))
                        .filter((slide) => slide !== placeholderEl && slide !== draggedEl)
                        .map((slide) => [slide.dataset.eventId, slide])
                );
                reassertOrder(track, expectedOrderIds, (id) =>
                    // The dragged card's own id maps back to the placeholder,
                    // not to draggedEl itself -- draggedEl stays wherever it
                    // currently is (irrelevant, it's out of flow) for the rest
                    // of the drag; only the placeholder's position is real
                    // order here.
                    draggedEl && id === draggedEl.dataset.eventId ? placeholderEl : byId.get(id)
                );
            });
            liveGuardObserver.observe(track, { childList: true, subtree: true });
        }

        event.preventDefault();

        // excludeEl: the placeholder itself -- see getSlideAfterCursor()'s
        // own doc comment for why it can't be a candidate to insert relative
        // to.
        const afterEl = getSlideAfterCursor(track, event.clientX, placeholderEl);
        if (afterEl !== lastAfterEl) {
            lastAfterEl = afterEl;

            // The placeholder moves; draggedEl itself never does again until
            // drop (see PLACEHOLDER_CLASS's own doc comment) -- excluding it
            // here is only defensive at this point (querySelectorAll would
            // still find it wherever it's currently parked), the placeholder
            // swap already carries the real work.
            const siblings = Array.from(track.querySelectorAll(SLIDE_SELECTOR)).filter(
                (el) => el !== draggedEl
            );
            const firstRects = new Map(siblings.map((el) => [el, el.getBoundingClientRect()]));

            if (afterEl == null) {
                track.appendChild(placeholderEl);
            } else if (afterEl !== placeholderEl) {
                track.insertBefore(placeholderEl, afterEl);
            }
            // Every reorder this drag itself makes is this drag's own new
            // "live intent" -- the guard observer above must track it, or it
            // would fight the very drag it exists to protect.
            expectedOrderIds = getLiveOrderIds();

            flipSiblings(track, siblings, firstRects, zoom);
        }

        // Recomputed from scratch every single frame, with NO dependency on
        // any earlier frame's own math: clearing the transform and
        // re-measuring gets draggedEl's current NATURAL (untransformed)
        // layout box -- wherever insertBefore/appendChild just put it, or
        // unchanged if this frame didn't reorder at all -- and the
        // transform is then simply "however far that natural box's own
        // top-left is from where the cursor's own fixed grab-point wants it
        // to be", divided by zoom (getEffectiveZoom()'s own doc comment
        // explains why a zoomed ancestor needs that). A single bad
        // measurement on one frame (a stale rect, a reorder landing between
        // two reads, anything) therefore can never compound into later
        // frames the way accumulating a running correction term did -- the
        // very next frame starts over from the DOM's own real position
        // again regardless. The slight scale is the rest of the "lifted"
        // read the CSS class's own box-shadow/opacity alone don't convey.
        draggedEl.style.transform = 'none';
        const naturalRect = draggedEl.getBoundingClientRect();
        const translateX = (event.clientX - grabOffsetX - naturalRect.left) / zoom;
        const translateY = (event.clientY - grabOffsetY - naturalRect.top) / zoom;
        draggedEl.style.transform = `translate(${translateX}px, ${translateY}px) scale(1.04)`;
    };

    const onPointerUp = (event) => {
        if (!draggedEl || event.pointerId !== pointerId) {
            return;
        }

        // Same staleness check onPointerMove does on every move (see its own
        // doc comment) -- covers the one gap that check alone can't: the
        // wrapper gets replaced, then the pointer is released WITHOUT moving
        // again first, so onPointerMove never gets another chance to catch
        // it before this handler would otherwise try to commit the drag
        // against the same dead track.
        if (wrapperEl.querySelector('.swiper-wrapper') !== track) {
            cancelDrag();
            return;
        }

        liveGuardObserver?.disconnect();
        liveGuardObserver = null;

        const finishedEl = draggedEl;
        const wasDragging = isDragging;

        document.removeEventListener('pointermove', onPointerMove);
        document.removeEventListener('pointerup', onPointerUp);
        document.removeEventListener('pointercancel', onPointerUp);

        if (wasDragging) {
            // Snaps straight to the resting slot instead of gliding there --
            // deliberately no transition/delay here. An earlier version
            // FLIP-animated this last step (matching flipSiblings()'s own
            // technique), but the user's own explicit feedback was that it
            // actively hurt the feel: every sibling has ALREADY visually
            // opened its gap live, during the drag itself (this module's
            // placeholder -- see PLACEHOLDER_CLASS's own doc comment --
            // already occupies the card's real target slot the whole time
            // it's held), so by the moment of release there is nothing left
            // to animate INTO; a further ~200ms glide just reads as the drop
            // lagging behind a layout that already settled.
            //
            // Commits the placeholder's own position as the real card's own
            // new DOM position -- finishedEl itself never moved past its
            // original drag-start slot for the whole gesture (only the
            // placeholder did), so without this swap the classList.remove()
            // below would reflow it straight back to where it STARTED,
            // undoing the entire drag.
            if (placeholderEl?.parentNode) {
                track.insertBefore(finishedEl, placeholderEl);
                placeholderEl.remove();
            }
            placeholderEl = null;
            finishedEl.style.transition = '';
            finishedEl.style.transform = '';
            finishedEl.classList.remove(DRAGGING_CLASS);
            persistOrder(wrapperEl);
            // Only for a real pointerup/pointercancel -- the visibilitychange
            // safety net below finishes a drag with no click of its own ever
            // following FROM this same gesture (the tab lost focus instead),
            // so there's nothing to suppress; { once: true } means an
            // unclaimed listener here would otherwise sit on the card
            // indefinitely and silently eat the NEXT unrelated, completely
            // legitimate click whenever the admin actually comes back to
            // open it.
            if (!event.forced) {
                suppressNextClick(finishedEl);
            }
        }

        draggedEl = null;
        pointerId = null;
        isDragging = false;
        lastAfterEl = undefined;
    };

    track.addEventListener('pointerdown', (event) => {
        // Ignore a second pointer while one drag is already active on this
        // track -- draggedEl/pointerId are single closure variables, so a
        // second pointerdown (a hybrid touch+mouse device, or two touch
        // points) would otherwise silently reassign pointerId out from under
        // the first drag: its own eventual pointerup would then fail the
        // `event.pointerId !== pointerId` guard in onPointerUp and never
        // fire, stranding that first drag's listeners/placeholder exactly
        // like an interrupted drag would (see the visibilitychange safety
        // net below for that same failure shape).
        if (draggedEl) {
            return;
        }
        // Left mouse button only -- a touch/pen pointer reports button -1 on
        // pointerdown and is intentionally left alone (this feature is
        // mouse-only, per its own field's instructions).
        if (event.pointerType === 'mouse' && event.button !== 0) {
            return;
        }
        const slide = event.target.closest(SLIDE_SELECTOR);
        if (!slide) {
            return;
        }

        // Selecting the block HERE, synchronously on pointerdown rather than
        // waiting for whatever click handling normally does it (modules/
        // events.js's own selectAncestorBlockFromClick, which only runs for
        // a NON-card click), matters specifically for the FIRST drag
        // attempt on a not-yet-selected block: confirmed in the field as
        // the pointerdown/move sequence itself running fine (no console
        // error, no missing listener) but the card visibly not moving --
        // consistent with Gutenberg's own block-selection re-render (it
        // mounts the block toolbar/outline on first selection) landing
        // mid-gesture and disrupting it, since ACF's ServerSideRender
        // subtree can get touched by that same re-render pass. Selecting
        // before any drag-tracking state is set up gets that settled
        // first; a block that's already selected makes this a no-op.
        const targetWp = getEditorWp();
        const clientId = wrapperEl.closest('[data-block]')?.getAttribute('data-block');
        if (
            clientId &&
            targetWp?.data?.select('core/block-editor')?.getSelectedBlockClientId() !== clientId
        ) {
            targetWp.data.dispatch('core/block-editor')?.selectBlock?.(clientId);
        }

        draggedEl = slide;
        pointerId = event.pointerId;
        startX = event.clientX;
        startY = event.clientY;
        isDragging = false;

        document.addEventListener('pointermove', onPointerMove);
        document.addEventListener('pointerup', onPointerUp);
        document.addEventListener('pointercancel', onPointerUp);
    });

    // Safety net for a drag left dangling by a real pointerup/pointercancel
    // that never arrives -- the Pointer Events spec doesn't guarantee either
    // fires on every focus-loss path while the mouse button is still
    // physically down (OS notification stealing focus, alt-tab, DevTools
    // grabbing focus, etc). Without this, an interrupted drag leaves the
    // placeholder (an empty dashed box) stuck permanently visible in the
    // live grid, liveGuardObserver running forever, and the document-level
    // pointermove/pointerup/pointercancel listeners above never removed --
    // confirmed as a real gap, not merely theoretical, since nothing else
    // in this module ever calls onPointerUp except those three listeners.
    // visibilitychange (not blur) is what actually fires reliably for
    // alt-tab/app-switch; synthesizing a matching pointerId is enough for
    // onPointerUp's own `event.pointerId !== pointerId` guard to pass and
    // run its normal cleanup path.
    document.addEventListener('visibilitychange', () => {
        if (document.hidden && draggedEl) {
            onPointerUp({ pointerId, forced: true });
        }
    });
};

export default function initRecommendedEventsReorder() {
    const attachAll = () => {
        document.querySelectorAll(WRAPPER_SELECTOR).forEach(attachWrapper);
    };

    attachAll();

    // Same rationale as initEventsPreviewScale()/initEventsAjax()
    // (events-ajax.js): ACF's ServerSideRender replaces this whole subtree
    // with fresh DOM nodes on every preview re-render (including the one
    // this module's own persistOrder() just triggered), so this keeps
    // watching for the block's lifetime instead of attaching once.
    //
    // Debounced (a microtask-scale delay, not a real wait) rather than
    // calling attachAll() directly from the observer -- this watches the
    // ENTIRE editor body, so any unrelated typing, another block's own
    // re-render, or ordinary Gutenberg UI churn anywhere on the page fires
    // it too, each one re-running a full document.querySelectorAll() scan.
    // attachAll() is idempotent either way (attachWrapper's own
    // track.dataset.jctcReorderBound guard), so this is purely about not
    // redoing that scan once per mutation record when a burst of them lands
    // in the same tick.
    let rescanScheduled = false;
    const scheduleRescan = () => {
        if (rescanScheduled) {
            return;
        }
        rescanScheduled = true;
        queueMicrotask(() => {
            rescanScheduled = false;
            attachAll();
        });
    };
    const observer = new MutationObserver(scheduleRescan);
    observer.observe(document.body, { childList: true, subtree: true });
}
